// SPDX-FileCopyrightText: 2025 LiveKit, Inc.
//
// SPDX-License-Identifier: Apache-2.0
import type { TypedEventEmitter as TypedEmitter } from '@livekit/typed-emitter';
import type { Span } from '@opentelemetry/api';
import { EventEmitter } from 'node:events';
import { APIConnectionError, APIError } from '../_exceptions.js';
import { log } from '../log.js';
import type { LLMMetrics } from '../metrics/base.js';
import { genAI, recordException, traceTypes, tracer } from '../telemetry/index.js';
import { type APIConnectOptions, intervalForRetry } from '../types.js';
import { AsyncIterableQueue, Task, delay, startSoon, toError } from '../utils.js';
import { type ChatContext, type ChatRole, type FunctionCall } from './chat_context.js';
import {
  type ToolChoice,
  type ToolContext,
  type ToolContextLike,
  toToolContext,
} from './tool_context.js';

export interface ChoiceDelta {
  role: ChatRole;
  content?: string;
  toolCalls?: FunctionCall[];
  extra?: Record<string, unknown>;
}

export interface CompletionUsage {
  completionTokens: number;
  promptTokens: number;
  promptCachedTokens: number;
  /** Tokens used to write to the prompt cache. */
  cacheCreationTokens?: number;
  totalTokens: number;
  /** The service tier used for processing (e.g. 'default', 'priority', 'flex'). */
  serviceTier?: string;
}

export interface ChatChunk {
  id: string;
  delta?: ChoiceDelta;
  usage?: CompletionUsage;
}

/**
 * Whether this chunk delivered generation the caller can see.
 *
 * Token counts and provider metadata (a gateway deployment stamp, a thought
 * signature) reach the caller without being output: they neither start the clock
 * on time-to-first-token nor give a retry anything to duplicate.
 */
export function hasResponse(chunk: ChatChunk): boolean {
  return Boolean(chunk.delta?.content || chunk.delta?.toolCalls?.length);
}

export interface CollectedResponse {
  text: string;
  toolCalls: FunctionCall[];
  usage?: CompletionUsage;
  /**
   * Provider-specific extra data accumulated across chunks
   * (e.g., xAI encrypted reasoning, Google thought signatures).
   */
  extra: Record<string, unknown>;
}

export interface LLMError {
  type: 'llm_error';
  timestamp: number;
  label: string;
  error: Error;
  recoverable: boolean;
}

export type LLMCallbacks = {
  ['metrics_collected']: (metrics: LLMMetrics) => void;
  ['error']: (error: LLMError) => void;
};

export abstract class LLM extends (EventEmitter as new () => TypedEmitter<LLMCallbacks>) {
  #prewarmTask?: Task<void>;
  #closed = false;

  constructor() {
    super();
  }

  abstract label(): string;

  /**
   * Get the model name/identifier for this LLM instance.
   *
   * @returns The model name if available, "unknown" otherwise.
   *
   * @remarks
   * Plugins should override this property to provide their model information.
   */
  get model(): string {
    return 'unknown';
  }

  /**
   * Get the provider name for this LLM instance.
   *
   * @returns The provider name if available, "unknown" otherwise.
   *
   * @remarks
   * Plugins should override this property to provide their provider information.
   */
  get provider(): string {
    return 'unknown';
  }

  /**
   * Returns a {@link LLMStream} that can be used to push text and receive LLM responses.
   */
  abstract chat({
    chatCtx,
    toolCtx,
    connOptions,
    parallelToolCalls,
    toolChoice,
    extraKwargs,
  }: {
    chatCtx: ChatContext;
    /**
     * Tools to advertise to the LLM. Accepts either a `ToolContext` instance or a raw
     * `(FunctionTool | ProviderTool)[]` array — the array form is normalized into a
     * `ToolContext` internally so callers don't have to construct one themselves.
     */
    toolCtx?: ToolContextLike;
    connOptions?: APIConnectOptions;
    parallelToolCalls?: boolean;
    toolChoice?: ToolChoice;
    extraKwargs?: Record<string, unknown>;
  }): LLMStream;

  /**
   * Pre-warm connection to the LLM service.
   *
   * Establishes DNS resolution and the TLS connection to the provider before the first inference
   * request, reducing time-to-first-token on the initial reply. Non-blocking (fire-and-forget) and
   * idempotent; calls made after {@link aclose} are ignored. Providers enable it by overriding
   * {@link _prewarmImpl}.
   */
  prewarm(): void {
    if (this.#closed || this._prewarmImpl === LLM.prototype._prewarmImpl) {
      return;
    }

    if (this.#prewarmTask) {
      return;
    }

    this.#prewarmTask = Task.from(async (controller) => {
      try {
        await this._prewarmImpl(controller.signal);
      } catch {
        // Prewarm is best-effort and must not affect session startup.
      }
    });
  }

  /**
   * Performs a provider-specific, token-free request that initializes DNS, TLS, authentication,
   * and keep-alive state.
   *
   * @remarks Exceptions are swallowed by {@link prewarm}. Implementations must honor `signal` so
   * {@link aclose} can cancel and await in-flight work.
   */
  protected async _prewarmImpl(_signal: AbortSignal): Promise<void> {}

  async aclose(): Promise<void> {
    this.#closed = true;
    if (this.#prewarmTask) {
      await this.#prewarmTask.cancelAndWait();
    }
  }
}

export abstract class LLMStream implements AsyncIterableIterator<ChatChunk> {
  protected output = new AsyncIterableQueue<ChatChunk>();
  protected queue = new AsyncIterableQueue<ChatChunk>();
  protected closed = false;
  protected abortController = new AbortController();
  protected _connOptions: APIConnectOptions;
  protected logger = log();

  #llm: LLM;
  #chatCtx: ChatContext;
  #toolCtx?: ToolContext;
  #llmRequestSpan?: Span;
  // Provider-known response ids collected from ChatChunks during the current
  // attempt; reset before each retry and written to the `llm_request_run` span.
  #providerRequestIds: string[] = [];

  constructor(
    llm: LLM,
    {
      chatCtx,
      toolCtx,
      connOptions,
    }: {
      chatCtx: ChatContext;
      toolCtx?: ToolContextLike;
      connOptions: APIConnectOptions;
    },
  ) {
    this.#llm = llm;
    this.#chatCtx = chatCtx;
    this.#toolCtx = toToolContext(toolCtx);
    this._connOptions = connOptions;
    this.monitorMetrics();
    this.abortController.signal.addEventListener('abort', () => {
      // TODO (AJS-37) clean this up when we refactor with streams
      this.output.close();
      this.closed = true;
    });

    // tells an enclosing `llm_node` span that this call is instrumented, so it does not
    // record the convention's attributes a second time. Read here rather than in mainTask,
    // which startSoon defers out of the node's context.
    genAI.markInferenceSpanRecorded();

    // this is a hack to immitate asyncio.create_task so that mainTask
    // is run **after** the constructor has finished. Otherwise we get
    // runtime error when trying to access class variables in the
    // `run` method.
    const runMainTask = async () => {
      try {
        await this.mainTask();
      } catch {
        // already surfaced via emitError; swallow to avoid unhandled rejection.
      } finally {
        this.queue.close();
      }
    };
    startSoon(() => {
      void runMainTask();
    });
  }

  /** The GenAI inference span's request side, per the OTel GenAI conventions. */
  private recordGenAIRequest(span: Span) {
    genAI.setRequestAttributes(span, {
      operation: traceTypes.GenAIOperationName.CHAT,
      provider: this.#llm.provider,
      model: this.#llm.model,
      stream: true,
      outputType: traceTypes.GenAIOutputType.TEXT,
    });
    genAI.setContentAttributes(span, {
      systemInstructions: genAI.toSystemInstructions(this.#chatCtx),
      inputMessages: genAI.toInputMessages(this.#chatCtx),
      toolDefinitions: this.#toolCtx ? genAI.toToolDefinitions(this.#toolCtx.functionTools) : [],
    });
  }

  private _mainTaskImpl = async (span: Span) => {
    this.#llmRequestSpan = span;
    span.setAttribute(traceTypes.ATTR_GEN_AI_REQUEST_MODEL, this.#llm.model);
    this.recordGenAIRequest(span);

    for (let i = 0; i < this._connOptions.maxRetry + 1; i++) {
      try {
        return await tracer.startActiveSpan(
          async (attemptSpan) => {
            attemptSpan.setAttribute(traceTypes.ATTR_RETRY_COUNT, i);
            // Reset per-attempt response ids; the metrics monitor populates this
            // as ChatChunks arrive.
            this.#providerRequestIds = [];
            try {
              return await this.run();
            } catch (error) {
              recordException(attemptSpan, toError(error));
              throw error;
            } finally {
              if (this.#providerRequestIds.length) {
                attemptSpan.setAttribute(
                  traceTypes.ATTR_PROVIDER_REQUEST_IDS,
                  this.#providerRequestIds,
                );
              }
            }
          },
          { name: 'llm_request_run' },
        );
      } catch (error) {
        if (error instanceof APIError) {
          const retryInterval = intervalForRetry(this._connOptions, i);

          if (this._connOptions.maxRetry === 0 || !error.retryable) {
            this.emitError({ error, recoverable: false });
            throw error;
          } else if (i === this._connOptions.maxRetry) {
            this.emitError({ error, recoverable: false });
            throw new APIConnectionError({
              message: `failed to generate LLM completion after ${this._connOptions.maxRetry + 1} attempts`,
              options: { retryable: false },
            });
          } else {
            this.emitError({ error, recoverable: true });
            this.logger.warn(
              { llm: this.#llm.label(), attempt: i + 1, error },
              `failed to generate LLM completion, retrying in ${retryInterval}ms`,
            );
          }

          if (retryInterval > 0) {
            await delay(retryInterval);
          }
        } else {
          this.emitError({ error: toError(error), recoverable: false });
          throw error;
        }
      }
    }
  };

  private mainTask = async () => {
    return tracer.startActiveSpan(async (span) => this._mainTaskImpl(span), {
      name: 'llm_request',
      endOnExit: false,
    });
  };

  private emitError({ error, recoverable }: { error: Error; recoverable: boolean }) {
    this.#llm.emit('error', {
      type: 'llm_error',
      timestamp: Date.now(),
      label: this.#llm.label(),
      error,
      recoverable,
    });
  }

  protected async monitorMetrics() {
    const startTime = process.hrtime.bigint();
    let ttft: bigint = BigInt(-1);
    let requestId = '';
    let usage: CompletionUsage | undefined;
    let completionStartTime: string | undefined;
    // accumulated for `gen_ai.output.messages`
    let responseContent = '';
    const toolCalls: FunctionCall[] = [];

    for await (const ev of this.queue) {
      if (this.abortController.signal.aborted) {
        break;
      }
      this.output.put(ev);
      requestId = ev.id;
      if (requestId && !this.#providerRequestIds.includes(requestId)) {
        this.#providerRequestIds.push(requestId);
      }
      // measured against generation, not the first chunk: a retry that follows a
      // contentless chunk would otherwise latch the clock on the failed attempt
      if (ttft === BigInt(-1) && hasResponse(ev)) {
        ttft = process.hrtime.bigint() - startTime;
        completionStartTime = new Date().toISOString();
      }
      if (ev.delta?.content) {
        responseContent += ev.delta.content;
      }
      if (ev.delta?.toolCalls?.length) {
        toolCalls.push(...ev.delta.toolCalls);
      }
      if (ev.usage) {
        usage = ev.usage;
      }
    }
    this.output.close();

    const duration = process.hrtime.bigint() - startTime;
    const durationMs = Math.trunc(Number(duration / BigInt(1000000)));
    const metrics: LLMMetrics = {
      type: 'llm_metrics',
      timestamp: Date.now(),
      requestId,
      ttftMs: ttft === BigInt(-1) ? -1 : Math.trunc(Number(ttft / BigInt(1000000))),
      durationMs,
      cancelled: this.abortController.signal.aborted,
      label: this.#llm.label(),
      completionTokens: usage?.completionTokens || 0,
      promptTokens: usage?.promptTokens || 0,
      promptCachedTokens: usage?.promptCachedTokens || 0,
      cacheCreationTokens: usage?.cacheCreationTokens || 0,
      totalTokens: usage?.totalTokens || 0,
      tokensPerSecond: (() => {
        if (durationMs <= 0) {
          return 0;
        }
        return (usage?.completionTokens || 0) / (durationMs / 1000);
      })(),
      metadata: {
        modelProvider: this.#llm.provider,
        modelName: this.#llm.model,
      },
    };

    if (this.#llmRequestSpan) {
      this.#llmRequestSpan.setAttribute(traceTypes.ATTR_LLM_METRICS, JSON.stringify(metrics));

      // the GenAI response side; the request side was recorded at span creation
      genAI.setUsageAttributes(this.#llmRequestSpan, metrics);

      const finishReason = genAI.finishReasonFor({
        functionCalls: toolCalls,
        interrupted: metrics.cancelled,
      });
      genAI.setResponseAttributes(this.#llmRequestSpan, {
        responseId: requestId || undefined,
        model: this.#llm.model,
        finishReasons: [finishReason],
        timeToFirstChunk: metrics.ttftMs >= 0 ? metrics.ttftMs / 1000 : undefined,
      });
      genAI.setContentAttributes(this.#llmRequestSpan, {
        outputMessages: genAI.toOutputMessages({
          text: responseContent,
          functionCalls: toolCalls,
          finishReason,
        }),
      });

      if (completionStartTime) {
        this.#llmRequestSpan.setAttribute(
          traceTypes.ATTR_LANGFUSE_COMPLETION_START_TIME,
          completionStartTime,
        );
      }

      // End the span now that metrics are collected
      this.#llmRequestSpan.end();
    }

    this.#llm.emit('metrics_collected', metrics);
  }

  protected abstract run(): Promise<void>;

  /** The function context of this stream. */
  get toolCtx(): ToolContext | undefined {
    return this.#toolCtx;
  }

  /** The initial chat context of this stream. */
  get chatCtx(): ChatContext {
    return this.#chatCtx;
  }

  /** The connection options for this stream. */
  get connOptions(): APIConnectOptions {
    return this._connOptions;
  }

  next(): Promise<IteratorResult<ChatChunk>> {
    return this.output.next();
  }

  close() {
    this.abortController.abort();
  }

  /**
   * Collect the entire stream into a single response.
   *
   * @example
   * ```ts
   * const response = await myLlm.chat({ chatCtx, toolCtx }).collect();
   *
   * for (const tc of response.toolCalls) {
   *   // execute the tool call...
   * }
   * ```
   */
  async collect(): Promise<CollectedResponse> {
    const textParts: string[] = [];
    const toolCalls: FunctionCall[] = [];
    let usage: CompletionUsage | undefined;
    const extra: Record<string, unknown> = {};

    try {
      for await (const chunk of this) {
        if (chunk.delta) {
          if (chunk.delta.content) {
            textParts.push(chunk.delta.content);
          }
          if (chunk.delta.toolCalls) {
            toolCalls.push(...chunk.delta.toolCalls);
          }
          if (chunk.delta.extra) {
            Object.assign(extra, chunk.delta.extra);
          }
        }
        if (chunk.usage !== undefined) {
          usage = chunk.usage;
        }
      }
    } finally {
      this.close();
    }

    return {
      text: textParts.join('').trim(),
      toolCalls,
      usage,
      extra,
    };
  }

  [Symbol.asyncIterator](): LLMStream {
    return this;
  }
}
